// ============================================================
// Tracker interno FlexShade
// ------------------------------------------------------------
// Sistema ligero de analítica propio (no depende de GA4/GTM).
// - Genera un visitor_id persistente (localStorage)
// - Genera un session_id por visita (sessionStorage)
// - Lee UTMs/referrer desde la URL (y persiste para conversiones)
// - Hace POST /api/events con navigator.sendBeacon cuando está disponible
// - Es best-effort: no rompe la UI si falla la red
// ============================================================

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const EVENTS_ENDPOINT = `${BACKEND_URL}/api/events`;

const VISITOR_KEY = "fs_visitor_id";
const SESSION_KEY = "fs_session_id";
const UTM_KEY = "fs_utm";

function uuid() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  // Fallback simple (no criptográfico)
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

export function getVisitorId() {
  try {
    let v = localStorage.getItem(VISITOR_KEY);
    if (!v) {
      v = uuid();
      localStorage.setItem(VISITOR_KEY, v);
    }
    return v;
  } catch {
    return null;
  }
}

export function getSessionId() {
  try {
    let s = sessionStorage.getItem(SESSION_KEY);
    if (!s) {
      s = uuid();
      sessionStorage.setItem(SESSION_KEY, s);
    }
    return s;
  } catch {
    return null;
  }
}

export function getStoredUTM() {
  try {
    return JSON.parse(sessionStorage.getItem(UTM_KEY) || "{}");
  } catch {
    return {};
  }
}

export function captureUTMFromURL() {
  try {
    const params = new URLSearchParams(window.location.search);
    const keys = ["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content"];
    const existing = getStoredUTM();
    let updated = { ...existing };
    let changed = false;
    keys.forEach((k) => {
      const v = params.get(k);
      if (v && updated[k] !== v) {
        updated[k] = v;
        changed = true;
      }
    });
    if (!updated.referrer && typeof document !== "undefined" && document.referrer) {
      updated.referrer = document.referrer;
      changed = true;
    }
    if (changed) sessionStorage.setItem(UTM_KEY, JSON.stringify(updated));
    return updated;
  } catch {
    return {};
  }
}

function sendEvent(payload) {
  try {
    const body = JSON.stringify(payload);
    // Intento 1: sendBeacon (robusto, no bloquea navegación)
    if (typeof navigator !== "undefined" && navigator.sendBeacon) {
      const blob = new Blob([body], { type: "application/json" });
      const ok = navigator.sendBeacon(EVENTS_ENDPOINT, blob);
      if (ok) return;
    }
    // Fallback: fetch con keepalive
    fetch(EVENTS_ENDPOINT, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body,
      keepalive: true,
      credentials: "omit",
    }).catch(() => {});
  } catch {
    // silenciosamente ignorar
  }
}

/**
 * Registra un evento de analítica interna.
 * @param {string} eventType - Uno de: page_view, lead_submit, catalog_download, whatsapp_click, phone_click, cta_click, form_open, form_abandon, section_view
 * @param {object} props - Propiedades adicionales del evento (ej: { cta: 'hero-primary' })
 */
export function trackEvent(eventType, props = {}) {
  try {
    const utm = getStoredUTM();
    const payload = {
      event_type: eventType,
      visitor_id: getVisitorId(),
      session_id: getSessionId(),
      path: typeof window !== "undefined" ? window.location.pathname + window.location.search : null,
      utm_source: utm.utm_source || null,
      utm_medium: utm.utm_medium || null,
      utm_campaign: utm.utm_campaign || null,
      utm_term: utm.utm_term || null,
      utm_content: utm.utm_content || null,
      referrer: utm.referrer || (typeof document !== "undefined" ? document.referrer : null) || null,
      props: props || {},
    };
    sendEvent(payload);
  } catch {
    // no-op
  }
}

/** Registra automáticamente un page_view y captura UTMs si vienen en la URL. */
export function trackPageView(extraProps = {}) {
  captureUTMFromURL();
  trackEvent("page_view", extraProps);
}

export default {
  trackEvent,
  trackPageView,
  getVisitorId,
  getSessionId,
  captureUTMFromURL,
  getStoredUTM,
};
